import { forwardRef, useId } from "react";
import type { ReactNode, SelectHTMLAttributes } from "react";
import "./Select.css";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  invalid?: boolean;
  options?: SelectOption[];
  placeholder?: string;
  size?: "sm" | "md" | "lg";
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    helperText,
    errorText,
    invalid,
    options,
    placeholder,
    size = "md",
    id,
    className,
    disabled,
    children,
    "aria-describedby": ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? `go4-select-${autoId}`;
  const helperId = `${selectId}-helper`;
  const errorId = `${selectId}-error`;
  const isInvalid = invalid || Boolean(errorText);

  const describedBy =
    [ariaDescribedBy, errorText ? errorId : null, helperText ? helperId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div className={cx("go4-field", disabled && "go4-field--disabled", className)}>
      {label ? (
        <label
          htmlFor={selectId}
          className={cx("go4-field__label", isInvalid && "go4-field__label--invalid")}
        >
          {label}
        </label>
      ) : null}
      <div className={cx("go4-select", `go4-select--${size}`, isInvalid && "go4-select--invalid")}>
        <select
          ref={ref}
          id={selectId}
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          className="go4-select__native"
          {...rest}
        >
          {placeholder ? (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          ) : null}
          {options
            ? options.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))
            : children}
        </select>
        <svg className="go4-select__caret" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {errorText ? (
        <p id={errorId} className="go4-field__error" role="alert">
          {errorText}
        </p>
      ) : helperText ? (
        <p id={helperId} className="go4-field__helper">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
